import { msalInstance } from "../auth/msalInstance";
import { storageScopes } from "../auth/msalConfig";
import { DRAWINGS_CONTAINER_URL } from "../config/api";

// Drawing storage in Azure Blob Storage (docs/implementation/DRAWING_STORAGE_PLAN.md, option A).
//
// Drawing files (a whole 100 MB plan set) live in a PRIVATE container of the `estassist` storage account instead
// of base64 text inside the database. Every signed-in estimator reaches it directly with their OWN Entra token
// (their group holds "Storage Blob Data Contributor" on the container) — no shared key, no SAS, nothing in the bundle.
//
// This module is the browser's whole storage client: upload in blocks (so 100 MB never has to fit one request,
// retries a failed block, reports progress), dedupe by content hash (the same set is never stored twice), and the
// pieces pdf.js needs to read just the pages that are opened (HTTP range requests).
//
// Dormant until VITE_DRAWINGS_CONTAINER_URL is set: `drawingsConfigured()` is false and nothing here is called.

export const STORAGE_API_VERSION = "2023-11-03";
export const BLOCK_BYTES = 8 * 1024 * 1024;               // one Put Block request
export const MAX_BLOCKS = 50000;                          // Azure's limit per blob
export const MAX_BLOB_BYTES = 250 * 1024 * 1024;          // a single drawing set (the app's own cap, well inside Azure's)
export const ALLOWED_DRAWING_TYPES = Object.freeze({ ".pdf": "application/pdf", ".png": "image/png", ".jpg": "image/jpeg", ".jpeg": "image/jpeg" });

export const drawingsConfigured = (base = DRAWINGS_CONTAINER_URL) => typeof base === "string" && /^https:\/\/[^/]+\/[^/?#]+\/?$/.test(base.trim());
const trimBase = (base) => String(base || "").trim().replace(/\/+$/, "");

// ── names ────────────────────────────────────────────────────────────────────
export const extOf = (fileName) => { const m = /\.[A-Za-z0-9]+$/.exec(String(fileName || "")); return m ? m[0].toLowerCase() : ""; };

/** Content-addressed name: the same bytes always land on the same blob, so a repeat upload is a no-op. */
export function blobNameForHash(sha256Hex, ext) {
  if (!/^[0-9a-f]{64}$/.test(String(sha256Hex || ""))) throw new Error("A SHA-256 hash is needed to name the file.");
  if (!ALLOWED_DRAWING_TYPES[ext]) throw new Error("Only PDF, PNG and JPEG drawings can be stored.");
  return `sets/${sha256Hex.slice(0, 2)}/${sha256Hex}${ext === ".jpeg" ? ".jpg" : ext}`;
}

export function blobUrl(name, base = DRAWINGS_CONTAINER_URL) {
  if (!drawingsConfigured(base)) throw new Error("Drawing storage is not configured.");
  const segs = String(name).split("/").filter(Boolean);
  if (!segs.length || segs.some(s => s === "." || s === "..")) throw new Error("Bad blob name.");
  return `${trimBase(base)}/${segs.map(encodeURIComponent).join("/")}`;
}

// ── hashing ──────────────────────────────────────────────────────────────────
export async function sha256OfBytes(buf) {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) throw new Error("This browser cannot hash files (a secure https page is required).");
  const d = await subtle.digest("SHA-256", buf);
  return [...new Uint8Array(d)].map(b => b.toString(16).padStart(2, "0")).join("");
}

// ── token ────────────────────────────────────────────────────────────────────
/**
 * The signed-in user's own Azure Storage token. Silent first; if consent / sign-in is needed and `interactive` is
 * set (call it from a click) a popup asks. null when unavailable — callers say so rather than failing quietly.
 */
export async function getStorageToken({ interactive = false } = {}) {
  try {
    const account = msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];
    if (!account) return null;
    try {
      const r = await msalInstance.acquireTokenSilent({ scopes: storageScopes, account });
      return r?.accessToken || null;
    } catch (e) {
      if (!interactive) return null;
      const r = await msalInstance.acquireTokenPopup({ scopes: storageScopes, account });
      return r?.accessToken || null;
    }
  } catch { return null; }
}

const headersFor = (token, extra = {}) => ({ Authorization: `Bearer ${token}`, "x-ms-version": STORAGE_API_VERSION, ...extra });

// ── upload ───────────────────────────────────────────────────────────────────
export const blockIdFor = (i) => btoa(String(i).padStart(8, "0"));                    // every id the same length
export const blockListXml = (ids) => `<?xml version="1.0" encoding="utf-8"?><BlockList>${ids.map(id => `<Latest>${id}</Latest>`).join("")}</BlockList>`;

/** -> { exists, bytes } — a HEAD for the blob (a content-addressed name existing means the bytes are already there). */
export async function headBlob(name, { token, base, fetchImpl = fetch } = {}) {
  const res = await fetchImpl(blobUrl(name, base), { method: "HEAD", headers: headersFor(token) });
  if (res.status === 404) return { exists: false, bytes: 0 };
  if (!res.ok) throw new Error(storageError(res.status, "check the drawing"));
  return { exists: true, bytes: Number(res.headers?.get?.("content-length")) || 0 };
}

export function storageError(status, doing) {
  if (status === 401) return `Your sign-in could not be used for drawing storage (401) — sign in again and retry.`;
  if (status === 403) return `You do not have access to drawing storage (403). Ask an administrator to add you to the drawings group, then try to ${doing} again.`;
  if (status === 404) return `Drawing storage was not found (404) — it may not be set up yet.`;
  if (status === 409) return `The drawing is being changed by someone else (409) — try again in a moment.`;
  return `Could not ${doing} (${status}).`;
}

async function withRetry(fn, { attempts = 3, signal } = {}) {
  let last;
  for (let i = 0; i < attempts; i++) {
    if (signal?.aborted) throw new Error("Cancelled");
    try { return await fn(); } catch (e) {
      last = e;
      if (e.noRetry || signal?.aborted) throw e;
    }
  }
  throw last;
}

/**
 * Upload a File/Blob as a block blob. Skips the upload when the (content-addressed) blob already exists.
 * -> { name, bytes, deduped }. onProgress(doneBytes, totalBytes). Throws a plain-language Error.
 */
export async function uploadBlob(file, name, { token, base, onProgress, signal, fetchImpl = fetch, blockBytes = BLOCK_BYTES, skipIfExists = true } = {}) {
  if (!token) throw new Error("Sign-in for drawing storage is not available.");
  const total = file.size;
  if (!total) throw new Error("The file is empty.");
  if (total > MAX_BLOB_BYTES) throw new Error(`That file is ${Math.round(total / 1048576)} MB; the limit is ${MAX_BLOB_BYTES / 1048576} MB.`);
  const blocks = Math.ceil(total / blockBytes);
  if (blocks > MAX_BLOCKS) throw new Error("The file is too large.");
  const url = blobUrl(name, base);
  const type = ALLOWED_DRAWING_TYPES[extOf(name)];
  if (!type) throw new Error("Only PDF, PNG and JPEG drawings can be stored.");

  if (skipIfExists) {
    const h = await headBlob(name, { token, base, fetchImpl });
    if (h.exists && h.bytes === total) { onProgress?.(total, total); return { name, bytes: total, deduped: true }; }
  }

  const ids = [];
  for (let i = 0; i < blocks; i++) {
    const id = blockIdFor(i);
    ids.push(id);
    const part = file.slice(i * blockBytes, Math.min(total, (i + 1) * blockBytes));
    await withRetry(async () => {
      const res = await fetchImpl(`${url}?comp=block&blockid=${encodeURIComponent(id)}`, { method: "PUT", headers: headersFor(token), body: part, signal });
      if (!res.ok) { const e = new Error(storageError(res.status, "upload the drawing")); e.noRetry = res.status === 401 || res.status === 403; throw e; }
    }, { signal });
    onProgress?.(Math.min(total, (i + 1) * blockBytes), total);
  }
  await withRetry(async () => {
    const res = await fetchImpl(`${url}?comp=blocklist`, { method: "PUT", headers: headersFor(token, { "x-ms-blob-content-type": type, "Content-Type": "application/xml" }), body: blockListXml(ids), signal });
    if (!res.ok) { const e = new Error(storageError(res.status, "finish the upload")); e.noRetry = res.status === 401 || res.status === 403; throw e; }
  }, { signal });
  return { name, bytes: total, deduped: false };
}

// ── reading ──────────────────────────────────────────────────────────────────
/**
 * What pdf.js needs to open a stored drawing WITHOUT downloading all of it: the blob URL, the user's token as a
 * header, and range requests (the pages that are opened are the only bytes fetched). `getDocument(pdfSource(...))`.
 */
export function pdfSource(name, { token, base } = {}) {
  if (!token) throw new Error("Sign-in for drawing storage is not available.");
  return { url: blobUrl(name, base), httpHeaders: headersFor(token), rangeChunkSize: 1 << 20, disableAutoFetch: true, disableStream: false };
}

/** A whole stored drawing as bytes (images, and small files). */
export async function fetchBlobBytes(name, { token, base, fetchImpl = fetch, signal } = {}) {
  const res = await fetchImpl(blobUrl(name, base), { headers: headersFor(token), signal });
  if (!res.ok) throw new Error(storageError(res.status, "open the drawing"));
  return new Uint8Array(await res.arrayBuffer());
}

// ── delete + end-to-end check ────────────────────────────────────────────────
export async function deleteBlob(name, { token, base, fetchImpl = fetch } = {}) {
  const res = await fetchImpl(blobUrl(name, base), { method: "DELETE", headers: headersFor(token) });
  if (res.status === 404) return false;
  if (!res.ok) throw new Error(storageError(res.status, "remove the test file"));
  return true;
}

// a valid 1x1 PNG (67 bytes) — the check writes this, reads it back, and deletes it
const CHECK_PNG_B64 = "iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==";
const checkBytes = () => Uint8Array.from(atob(CHECK_PNG_B64), c => c.charCodeAt(0));

/**
 * Walks the whole chain a real upload needs and reports each step in plain words — so a missing setting
 * (build value, consent, role, CORS) is named instead of surfacing later as a failed upload.
 * -> [{ key, label, ok, detail }]; stops at the first failure.
 * Deps are injectable for tests: { base, getToken, fetchImpl, now }.
 */
export async function runStorageCheck({ base = DRAWINGS_CONTAINER_URL, getToken = () => getStorageToken({ interactive: true }), fetchImpl = fetch, now = Date.now } = {}) {
  const out = [];
  const step = (key, label, ok, detail = "") => { out.push({ key, label, ok, detail }); return ok; };

  if (!step("config", "Drawing storage is configured in this build", drawingsConfigured(base),
    "VITE_DRAWINGS_CONTAINER_URL is not set in this build. Set vite-drawings-container-url in Key Vault; the next deploy picks it up.")) return out;

  let token = null;
  try { token = await getToken(); } catch { token = null; }
  if (!step("token", "Got an Azure Storage sign-in for you", !!token,
    "No Storage token. Allow the sign-in popup if one was blocked. If it keeps failing, the app registration is missing the Azure Storage ▸ user_impersonation permission or its admin consent.")) return out;

  const name = `tests/check-${now()}.png`;
  const bytes = checkBytes();
  const file = new Blob([bytes], { type: "image/png" });
  try {
    await uploadBlob(file, name, { token, base, fetchImpl, skipIfExists: false });
    step("write", "Wrote a test file to the drawings container", true);
  } catch (e) {
    step("write", "Wrote a test file to the drawings container", false, e.message + " (A 'CORS' / 'Failed to fetch' message means the storage account's CORS rule does not allow this site.)");
    return out;
  }
  try {
    const back = await fetchBlobBytes(name, { token, base, fetchImpl });
    const same = back.length === bytes.length && back.every((b, i) => b === bytes[i]);
    if (!step("read", "Read the test file back unchanged", same, same ? "" : "The file came back different from what was written.")) return out;
  } catch (e) { step("read", "Read the test file back unchanged", false, e.message); return out; }
  try {
    await deleteBlob(name, { token, base, fetchImpl });
    step("delete", "Removed the test file", true);
  } catch (e) { step("delete", "Removed the test file", false, e.message + ` The test file ${name} was left in the container.`); }
  return out;
}

// ── sheets that live in a stored set ─────────────────────────────────────────
// A set-backed sheet has no file of its own; the app gives it a POINTER in the place a data URL used to sit
// (`blob://sets/ab/<sha256>.pdf`), and the PDF loader resolves it to ranged reads of the stored file.
export const BLOB_POINTER = "blob://";
export const blobPointer = (blobName) => `${BLOB_POINTER}${blobName}`;
export const isBlobPointer = (s) => typeof s === "string" && s.startsWith(BLOB_POINTER);
export const blobNameOfPointer = (s) => (isBlobPointer(s) ? s.slice(BLOB_POINTER.length) : null);

/** One byte range [begin, endExclusive) of a stored file, with a FRESH token per call (a drawing can stay open past one token's hour). */
export async function fetchBlobRange(name, begin, endExclusive, { token, base, fetchImpl = fetch, signal } = {}) {
  if (!token) throw new Error("Sign-in for drawing storage is not available.");
  const res = await fetchImpl(blobUrl(name, base), { headers: headersFor(token, { Range: `bytes=${begin}-${endExclusive - 1}` }), signal });
  if (!(res.ok || res.status === 206)) throw new Error(storageError(res.status, "open the drawing"));
  return new Uint8Array(await res.arrayBuffer());
}
